/* istanbul ignore file */
//TODO Tech Debt: Rip all non-changing CMS stuff (all of it?) out of the store
import { Module } from 'vuex';
import { AppState } from '@/store';
import { Config } from '@/environment';
import { createCmsImageUrls, createCmsAnimationUrls } from './assets';

import { CmsLink, PromoBanner, LargeOrderPromo, LargeOrderPromoCard, JoinRewards, LegalCopy, FeaturedPCE, PickupModality, CreateAccountSignInContentFragment, OrderConfirmationCreateAccountExperience } from '@/models/cms';
import { prefixValues } from '@/helpers/util';

const MUTATION_NAMES = {
  SET_PROMO_TOP_GUEST_DESKTOP: 'setPromoTopGuestDesktop',
  SET_PROMO_TOP_GUEST_MOBILE: 'setPromoTopGuestMobile',
  SET_PROMO_TOP_AUTHENTICATED_DESKTOP: 'setPromoTopAuthenticatedDesktop',
  SET_PROMO_TOP_AUTHENTICATED_MOBILE: 'setPromoTopAuthenticatedMobile',
  SET_PROMO_TOP_GUEST_DESKTOP_CANADA: 'setPromoTopGuestDesktopCanada',
  SET_PROMO_TOP_GUEST_MOBILE_CANADA: 'setPromoTopGuestMobileCanada',
  SET_PROMO_TOP_AUTHENTICATED_DESKTOP_CANADA: 'setPromoTopAuthenticatedDesktopCanada',
  SET_PROMO_TOP_AUTHENTICATED_MOBILE_CANADA: 'setPromoTopAuthenticatedMobileCanada',
  SET_PROMO_BOTTOM: 'setPromoBottom',
  SET_PROMO_BOTTOM_CANADA: 'setPromoBottomCanada',
  SET_PROMO_BANNER_REWARDS_GUEST: 'setPromoBannerRewardsGuest',
  SET_PROMO_BANNER_REWARDS_AUTHENTICATED_NOT_ENROLLED: 'setPromoBannerRewardsAuthenticatedNotEnrolled',
  SET_HEADER_LINKS: 'setHeaderLinks',
  SET_FEATURED_HEADER: 'setFeaturedHeader',
  SET_LOYALTY_BANNER: 'setLoyaltyBanner',
  SET_LARGE_ORDER_PROMO: 'setLargeOrderPromo',
  SET_GROUP_ORDER_CARD: 'setGroupOrderCard',
  SET_CATERING_CARD: 'setCateringCard',
  SET_CATERING_CARD_CANADA: 'setCateringCardCanada',
  SET_ACCOUNT_MODAL_JOIN_REWARDS: 'setAccountModalJoinRewards',
  SET_LEGAL_COPY: 'setLegalCopy',
  SET_SIGN_IN_FRAGMENT: 'setSignInFragment',
  SET_CREATE_ACCOUNT_FRAGMENT: 'setCreateAccountFragment',
  SET_GUEST_ORDER_CONFIRMATION_EXPERIENCE: 'setGuestOrderConfirmationExperience'
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, 'cms/');

export interface CmsModuleState {
  baseUrl: string;
  baseUrlCanada: string;

  header: {
    links: CmsLink[];
  };

  home: {
    menuItemLinks: CmsImageLink[];
    title: string;
    description: string;
  };

  images: ReturnType<typeof createCmsImageUrls>;
  animations: ReturnType<typeof createCmsAnimationUrls>;
  promoBannerTopGuestDesktop: PromoBanner | null;
  promoBannerTopGuestMobile: PromoBanner | null;
  promoBannerTopAuthenticatedDesktop: PromoBanner | null;
  promoBannerTopAuthenticatedMobile: PromoBanner | null;
  promoBannerTopGuestDesktopCanada: PromoBanner | null;
  promoBannerTopGuestMobileCanada: PromoBanner | null;
  promoBannerTopAuthenticatedDesktopCanada: PromoBanner | null;
  promoBannerTopAuthenticatedMobileCanada: PromoBanner | null;
  promoBannerBottom: PromoBanner | null;
  promoBannerBottomCanada: PromoBanner | null;
  promoBannerRewardsGuest: PromoBanner | null;
  promoBannerRewardsAuthenticatedNotEnrolled: PromoBanner | null;
  featuredHeader: string;
  featuredPCE: FeaturedPCE;
  createAccountForm: CreateAccountSignInContentFragment | null;
  signInContentFragment: CreateAccountSignInContentFragment | null;

  largeOrderPromo: LargeOrderPromo | null;
  groupOrderCard: LargeOrderPromoCard | null;
  cateringCard: LargeOrderPromoCard | null;
  cateringCardCanada: LargeOrderPromoCard | null;
  joinRewards: JoinRewards | null;
  legalCopy: LegalCopy | null;
  fullNutritionURL: string | undefined;
  pickupModality: PickupModality | undefined;
  orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience | null;
}

export function makeCmsModule(config: Config): Module<CmsModuleState, AppState> {
  return {
    namespaced: true,
    state: buildDefaultState(config),
    mutations: {
      [MUTATION_NAMES.SET_SIGN_IN_FRAGMENT]: (state, signInFragment: CreateAccountSignInContentFragment) => {
        state.signInContentFragment = signInFragment;
      },
      [MUTATION_NAMES.SET_CREATE_ACCOUNT_FRAGMENT]: (state, createAccountFragment: CreateAccountSignInContentFragment) => {
        state.createAccountForm = createAccountFragment;
      },
      [MUTATION_NAMES.SET_HEADER_LINKS]: (state, headerLinks: CmsLink[]) => {
        state.header.links = headerLinks;
      },
      // Product requested we remove the distinction between auth and guest for the hero (PROMO_TOP) banner. Changing it here so we can revert on request.
      // If the revert hasnt happened in a few months we should clean it up properly. Also see cms build config.
      [MUTATION_NAMES.SET_PROMO_TOP_GUEST_DESKTOP]: (state, promo: PromoBanner) => {
        state.promoBannerTopGuestDesktop = promo;
        state.promoBannerTopAuthenticatedDesktop = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_AUTHENTICATED_DESKTOP]: (state, promo: PromoBanner) => {
        state.promoBannerTopAuthenticatedDesktop = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_GUEST_MOBILE]: (state, promo: PromoBanner) => {
        state.promoBannerTopGuestMobile = promo;
        state.promoBannerTopAuthenticatedMobile = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_AUTHENTICATED_MOBILE]: (state, promo: PromoBanner) => {
        state.promoBannerTopAuthenticatedMobile = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_GUEST_DESKTOP_CANADA]: (state, promo: PromoBanner) => {
        state.promoBannerTopGuestDesktopCanada = promo;
        state.promoBannerTopAuthenticatedMobileCanada = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_AUTHENTICATED_DESKTOP_CANADA]: (state, promo: PromoBanner) => {
        state.promoBannerTopAuthenticatedDesktopCanada = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_GUEST_MOBILE_CANADA]: (state, promo: PromoBanner) => {
        state.promoBannerTopGuestMobileCanada = promo;
        state.promoBannerTopAuthenticatedMobileCanada = promo;
      },
      [MUTATION_NAMES.SET_PROMO_TOP_AUTHENTICATED_MOBILE_CANADA]: (state, promo: PromoBanner) => {
        state.promoBannerTopAuthenticatedMobileCanada = promo;
      },
      [MUTATION_NAMES.SET_PROMO_BOTTOM]: (state, promo: PromoBanner) => {
        state.promoBannerBottom = promo;
      },
      [MUTATION_NAMES.SET_PROMO_BOTTOM_CANADA]: (state, promo: PromoBanner) => {
        state.promoBannerBottomCanada = promo;
      },
      [MUTATION_NAMES.SET_PROMO_BANNER_REWARDS_GUEST]: (state, promo: PromoBanner) => {
        state.promoBannerRewardsGuest = promo;
      },
      [MUTATION_NAMES.SET_PROMO_BANNER_REWARDS_AUTHENTICATED_NOT_ENROLLED]: (state, promo: PromoBanner) => {
        state.promoBannerRewardsAuthenticatedNotEnrolled = promo;
      },
      [MUTATION_NAMES.SET_FEATURED_HEADER]: (state, featuredHeader: string) => {
        state.featuredHeader = featuredHeader;
      },
      [MUTATION_NAMES.SET_LARGE_ORDER_PROMO]: (state, largOrderPromo: LargeOrderPromo) => {
        state.largeOrderPromo = largOrderPromo;
      },
      [MUTATION_NAMES.SET_GROUP_ORDER_CARD]: (state, groupOrderCard: LargeOrderPromoCard) => {
        state.groupOrderCard = groupOrderCard;
      },
      [MUTATION_NAMES.SET_CATERING_CARD]: (state, cateringCard: LargeOrderPromoCard) => {
        state.cateringCard = cateringCard;
      },
      [MUTATION_NAMES.SET_CATERING_CARD_CANADA]: (state, cateringCard: LargeOrderPromoCard) => {
        state.cateringCardCanada = cateringCard;
      },
      [MUTATION_NAMES.SET_ACCOUNT_MODAL_JOIN_REWARDS]: (state, joinRewards: JoinRewards) => {
        state.joinRewards = joinRewards;
      },
      [MUTATION_NAMES.SET_LEGAL_COPY]: (state, legalCopy: LegalCopy) => {
        state.legalCopy = legalCopy;
      },
      [MUTATION_NAMES.SET_GUEST_ORDER_CONFIRMATION_EXPERIENCE]: (state, orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience) => {
        state.orderConfirmationCreateAccountExperience = orderConfirmationCreateAccountExperience;
      },
    },
  };
}

function buildDefaultState(config: Config): CmsModuleState {
  return {
    baseUrl: config.cms.baseUrl,
    baseUrlCanada: config.cms.baseUrlCanada,
    header: {
      links: config.cms.headerLinks || [],
    },
    home: {
      menuItemLinks: [],
      title: config.cms.titleAndDescription?.title ?? '',
      description: config.cms.titleAndDescription?.description ?? '',
    },
    images: createCmsImageUrls(config.cms.baseUrl),
    animations: createCmsAnimationUrls(config.cms.baseUrl),
    promoBannerTopGuestDesktop: config.cms.promoBannerTopGuestDesktop || null,
    promoBannerTopGuestMobile: config.cms.promoBannerTopGuestMobile || null,
    promoBannerTopAuthenticatedDesktop: config.cms.promoBannerTopAuthenticatedDesktop || null,
    promoBannerTopAuthenticatedMobile: config.cms.promoBannerTopAuthenticatedMobile || null,
    promoBannerTopGuestDesktopCanada: config.cms.promoBannerTopGuestDesktopCanada || null,
    promoBannerTopGuestMobileCanada: config.cms.promoBannerTopGuestMobileCanada || null,
    promoBannerTopAuthenticatedDesktopCanada: config.cms.promoBannerTopAuthenticatedDesktopCanada || null,
    promoBannerTopAuthenticatedMobileCanada: config.cms.promoBannerTopAuthenticatedMobileCanada || null,
    promoBannerBottom: config.cms.promoBannerBottom || null,
    promoBannerBottomCanada: config.cms.promoBannerBottomCanada || null,
    promoBannerRewardsGuest: config.cms.promoBannerRewardsGuest || null,
    promoBannerRewardsAuthenticatedNotEnrolled: config.cms.promoBannerRewardsAuthenticatedNotEnrolled || null,
    featuredHeader: `FEATURED`,
    featuredPCE: config.cms.featuredPCE || null,
    largeOrderPromo: config.cms.largeOrderPromo || null,
    groupOrderCard: config.cms.groupOrderCard || null,
    cateringCard: config.cms.cateringCard || null,
    cateringCardCanada: config.cms.cateringCardCanada || null,
    joinRewards: config.cms.joinRewards || null,
    legalCopy: config.cms.legalCopy || null,
    fullNutritionURL: config.cms.fullNutritionURL,
    pickupModality: config.cms.pickupModality,
    createAccountForm: config.cms.createAccountFormFragment,
    signInContentFragment: config.cms.signInModalFragment,
    orderConfirmationCreateAccountExperience: config.cms.orderConfirmationCreateAccountExperience
  };
}
